import { useCallback, useMemo } from "react";
import { useBundleTokens } from "@/hooks/use-bundle-tokens";
import { assembleCompleteSupportBundle } from "@/utils/support-bundle";
import type { PlainEnvironment } from "@/queries/environment-queries";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import type { ExecutionBundleSource } from "@/utils/support-bundle";

export const useCompleteSupportBundle = (
    environment: PlainEnvironment,
    agentExecutions?: PlainAgentExecution[],
) => {
    const supportBundleUrl = environment.status?.environmentUrls?.supportBundle;

    // Derive stable execution ID list and URL map from agent executions that have a support bundle URL.
    const executionsWithBundles = useMemo(() => {
        if (!agentExecutions?.length) {
            return [];
        }
        return agentExecutions
            .filter((exec): exec is PlainAgentExecution & { status: { supportBundleUrl: string } } =>
                !!exec.status?.supportBundleUrl,
            )
            .map((exec) => ({
                executionId: exec.id,
                supportBundleUrl: exec.status.supportBundleUrl,
            }));
    }, [agentExecutions]);

    const executionIds = useMemo(
        () => executionsWithBundles.map((e) => e.executionId),
        [executionsWithBundles],
    );

    const { enableTokenFetch, isTokenReady, environmentLogsToken, executionTokens } = useBundleTokens({
        environmentId: supportBundleUrl ? environment.id : undefined,
        executionIds,
    });

    const isSupportBundleAvailable = !!supportBundleUrl;

    const downloadSupportBundle = useCallback(async () => {
        // Build environment bundle option if available.
        const environmentBundle =
            supportBundleUrl && environmentLogsToken
                ? { supportBundleUrl, logsToken: environmentLogsToken }
                : undefined;

        // Build execution bundle sources from executions that have both a URL and a token.
        const executionBundles: ExecutionBundleSource[] = executionsWithBundles
            .map((exec) => {
                const token = executionTokens.get(exec.executionId);
                if (!token) {
                    return undefined;
                }
                return {
                    executionId: exec.executionId,
                    supportBundleUrl: exec.supportBundleUrl,
                    token,
                };
            })
            .filter((source): source is ExecutionBundleSource => !!source);

        // Need at least one source to proceed.
        if (!environmentBundle && executionBundles.length === 0) {
            return;
        }

        // Filename: use primary execution ID when agent executions exist, otherwise environment ID.
        const filename =
            executionsWithBundles.length > 0
                ? `support-bundle-${executionsWithBundles[0].executionId}.zip`
                : `support-bundle-${environment.id}.zip`;

        await assembleCompleteSupportBundle({
            environmentBundle,
            executionBundles,
            filename,
        });
    }, [supportBundleUrl, environmentLogsToken, executionsWithBundles, executionTokens, environment.id]);

    return {
        downloadSupportBundle,
        isSupportBundleAvailable,
        enableTokenFetch,
        isTokenReady,
    };
};
